import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { formatDate, timeAgo } from './fileLibrary';

export const DEFAULT_MAX_CHARS = 10000;

const PLACEHOLDER = [
  'Examples:',
  "- If I add a file about a specific project (like Maple Ridge), put it in that project's folder.",
  '- Permits, surveys and plats go in Due Diligence inside the project folder.',
  '- Market reports and comps go in Market Research, sorted by ZIP code.',
  '- My folders should be organized like this:',
  '    Projects / <project name> / Due Diligence, Plans, Budget, Marketing',
  '- Name files like 2026-10-07 Project - Description.',
].join('\n');

const EMPTY_ENTRY = { status: 'loading', data: null, error: null, draft: null, saving: false };

const formatCount = (n) => Number(n || 0).toLocaleString('en-US');

// A backend without the route (404, or 405 for the method) has no filing preferences yet.
const isUnavailable = (err) => err?.status === 404 || err?.status === 405;

function normalizePrefs(raw) {
  const max = Number(raw?.max_chars);
  return {
    preferences: typeof raw?.preferences === 'string' ? raw.preferences : '',
    max_chars: Number.isFinite(max) && max > 0 ? max : DEFAULT_MAX_CHARS,
    updated_at: raw?.updated_at || null,
    updated_by_user_id: raw?.updated_by_user_id ?? null,
    scope: raw?.scope || null,
    company_id: raw?.company_id ?? null,
    editable: raw?.editable !== false,
  };
}

export function filingPrefsErrorText(err, maxChars) {
  if (err?.code === 'too_long') {
    const max = Number(err?.body?.max_chars) || maxChars || DEFAULT_MAX_CHARS;
    return `Filing preferences are over the ${formatCount(max)}-character limit. Shorten them and save again.`;
  }
  return `Couldn't save filing preferences: ${err?.message || 'please try again.'}`;
}

/**
 * Filing preferences of each library shown in this browser session, keyed by
 * scope ('personal' | 'company:<id>'): { status, data, error, draft, saving }.
 * draft is the unsaved text (null: no edits), so an edit survives switching
 * views and libraries. A new resetKey (browser closed, another user's or
 * company's library) drops everything, and answers to older requests are ignored.
 */
export function useFilingPreferences({ client, scopeKey, resetKey, track, showNotice }) {
  const [store, setStore] = useState({});
  const storeRef = useRef(store);
  storeRef.current = store;
  const epochRef = useRef(0);
  const seqRef = useRef(0);
  const latestRef = useRef({});

  // Layout effect: runs before the section's load effect, so a load for the new
  // library is never mistaken for one from the old.
  useLayoutEffect(() => {
    epochRef.current += 1;
    setStore((prev) => (Object.keys(prev).length ? {} : prev));
  }, [resetKey]);

  const patch = useCallback((key, update) => {
    setStore((prev) => {
      const current = prev[key] || EMPTY_ENTRY;
      const next = typeof update === 'function' ? update(current) : { ...current, ...update };
      return next === current ? prev : { ...prev, [key]: next };
    });
  }, []);

  // Each request takes a number; a load answer only counts if nothing newer
  // (another load, or a save) started for that library since.
  const stamp = useCallback((key) => {
    seqRef.current += 1;
    latestRef.current[key] = seqRef.current;
    return { seq: seqRef.current, epoch: epochRef.current };
  }, []);

  const load = useCallback(async () => {
    const key = scopeKey;
    const { seq, epoch } = stamp(key);
    const isLatest = () => epochRef.current === epoch && latestRef.current[key] === seq;
    // Already loaded: keep showing it while it refreshes.
    patch(key, (s) => (s.data ? s : { ...s, status: 'loading', error: null }));
    try {
      const data = normalizePrefs(await client.getFilingPreferences());
      if (!isLatest()) return;
      patch(key, (s) => ({
        ...s,
        status: 'ready',
        data,
        error: null,
        draft: s.draft === data.preferences ? null : s.draft,
      }));
    } catch (err) {
      if (!isLatest()) return;
      if (isUnavailable(err)) {
        patch(key, { status: 'missing', data: null, error: null, draft: null });
      } else {
        patch(key, (s) => (s.data ? s : { ...s, status: 'error', error: err?.message || 'Filing preferences could not be loaded.' }));
      }
    }
  }, [client, patch, scopeKey, stamp]);

  const change = useCallback((text) => {
    patch(scopeKey, (s) => {
      if (!s.data) return s;
      const draft = text === s.data.preferences ? null : text;
      return draft === s.draft ? s : { ...s, draft };
    });
  }, [patch, scopeKey]);

  const discard = useCallback(() => {
    patch(scopeKey, (s) => (s.draft === null ? s : { ...s, draft: null }));
  }, [patch, scopeKey]);

  const save = useCallback(async () => {
    const key = scopeKey;
    const entry = storeRef.current[key];
    if (!entry?.data || entry.saving || entry.draft === null) return false;
    const text = entry.draft;
    const { epoch } = stamp(key);
    patch(key, { saving: true });
    let raw;
    try {
      // Tracked: Escape and the backdrop don't close the browser mid-save.
      raw = await track(() => client.saveFilingPreferences(text));
    } catch (err) {
      if (epochRef.current === epoch) patch(key, { saving: false });
      showNotice('error', filingPrefsErrorText(err, entry.data.max_chars));
      return false;
    }
    const data = normalizePrefs({ ...entry.data, preferences: text, updated_at: new Date().toISOString(), ...(raw || {}) });
    if (epochRef.current === epoch) {
      patch(key, (s) => ({
        ...s,
        status: 'ready',
        saving: false,
        data,
        // Typed on while it saved: keep that. Otherwise show the text as stored.
        draft: s.draft === null || s.draft === text || s.draft === data.preferences ? null : s.draft,
      }));
    }
    showNotice('success', data.preferences.trim()
      ? 'Filing preferences saved. Atlas will follow them for new files.'
      : 'Filing preferences cleared.');
    return true;
  }, [client, patch, scopeKey, showNotice, stamp, track]);

  const hasUnsaved = useCallback(() => Object.values(storeRef.current).some((s) => s.draft !== null), []);

  // Drop the unsaved text of every library (the loaded preferences stay).
  const discardAll = useCallback(() => {
    setStore((prev) => {
      if (!Object.values(prev).some((s) => s.draft !== null)) return prev;
      return Object.fromEntries(Object.entries(prev).map(([key, s]) => [key, s.draft === null ? s : { ...s, draft: null }]));
    });
  }, []);

  return {
    entry: store[scopeKey] || EMPTY_ENTRY,
    load,
    change,
    discard,
    discardAll,
    save,
    hasUnsaved,
  };
}

/**
 * Filing preferences: free-text rules for how this library is filed. Auto-sort,
 * folder suggestions and Atlas in chat follow them. One set per library.
 * A card on the Library settings page (LibrarySettingsView).
 */
export default function FilingPreferencesSection({
  entry,
  onLoad,
  onChange,
  onSave,
  onDiscard,
  isCompany,
  companyName,
  viewingUserLabel,
  autoSortOff,
}) {
  useEffect(() => { onLoad(); }, [onLoad]);

  const { status, data, error, draft, saving } = entry;
  const missing = status === 'missing';
  let subtitle = 'Tell Atlas how to file your files. Auto-sort, folder suggestions and Atlas in chat follow these.';
  if (isCompany) {
    subtitle = `How files in the ${companyName} library should be filed. Shared with everyone in ${companyName}; anyone can edit.`;
  } else if (viewingUserLabel) {
    subtitle = `How files in ${viewingUserLabel}'s library should be filed. Auto-sort, folder suggestions and Atlas in chat follow these.`;
  }

  let body;
  if (missing) {
    body = <p className="fl-muted">Filing preferences aren&apos;t available yet.</p>;
  } else if (!data && status === 'error') {
    body = (
      <div className="fl-inline-error" role="alert">
        <IoAlertCircleOutline size={16} aria-hidden="true" />
        <span>{error}</span>
        <button type="button" className="fl-link-btn" onClick={() => onLoad()}>Retry</button>
      </div>
    );
  } else if (!data) {
    body = <div className="fl-skeleton-row fl-prefs-skeleton" aria-busy="true" aria-label="Loading filing preferences" />;
  } else {
    const text = draft ?? data.preferences;
    const max = data.max_chars;
    const dirty = draft !== null;
    const { editable } = data;
    // Only saved, non-empty preferences have a "Last updated": updated_at also
    // moves when auto-sort is switched, which would date an empty box.
    const ago = data.preferences.trim() ? timeAgo(data.updated_at) : null;
    body = (
      <>
        <textarea
          className="fl-prefs-text"
          aria-label="Filing preferences"
          aria-describedby="fl-prefs-note"
          value={text}
          maxLength={max}
          placeholder={PLACEHOLDER}
          readOnly={!editable}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
            e.preventDefault();
            if (dirty && !saving && editable) onSave();
          }}
        />
        <div className="fl-prefs-meta">
          <p id="fl-prefs-note" className="fl-prefs-note">
            These apply to new files and to Atlas&apos;s suggestions. To reorganize files you already have, ask Atlas in chat.
          </p>
          <span className={`fl-prefs-count${text.length > max * 0.9 ? ' is-near' : ''}`} aria-live="polite">
            {formatCount(text.length)} / {formatCount(max)} characters
          </span>
        </div>
        <div className="fl-prefs-actions">
          {ago ? (
            <span className="fl-prefs-updated" title={formatDate(data.updated_at, { withTime: true })}>
              Last updated {ago === 'now' ? 'just now' : `${ago} ago`}
            </span>
          ) : null}
          {editable ? (
            <>
              <button type="button" className="fl-btn" disabled={!dirty || saving} onClick={() => onDiscard()}>
                Discard changes
              </button>
              <button type="button" className="fl-btn fl-btn-primary" disabled={!dirty || saving} onClick={() => onSave()}>
                {saving ? <span className="fl-spinner" aria-hidden="true" /> : null}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <span className="fl-muted">You can read these but not change them.</span>
          )}
        </div>
      </>
    );
  }

  return (
    <section className="fl-settings-card fl-prefs" aria-labelledby="fl-prefs-title">
      <div className="fl-settings-card-head">
        <h4 id="fl-prefs-title" className="fl-settings-card-title">Filing preferences</h4>
        {missing ? null : <p className="fl-settings-card-desc">{subtitle}</p>}
        {!missing && autoSortOff ? (
          <p className="fl-settings-card-desc">Auto-sort is off, so Atlas uses these to suggest folders instead of filing.</p>
        ) : null}
      </div>
      <div className="fl-settings-card-body">{body}</div>
    </section>
  );
}
